import React, { useEffect, useState } from 'react';
import client from '../../../../lib/feathers';
import VisitorConversionCard from './VisitorConversionCard';
import BenchmarkingCard from './BenchmarkingCard';
import TimingHeatmap from './TimingHeatmap';
import ConversionMetricsCard from './ConversionMetricsCard';
import BehavioralCard from './BehavioralCard';
import UpgradePrompt from './UpgradePrompt';

export default function PassportAnalyticsPanel({ passportId, listingId, isAdmin = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use admin or industry service based on context
        const servicePath = isAdmin ? 'admin/passport-enhanced-stats' : 'industry/passport-enhanced-stats';
        const service = client.service(servicePath);
        const result = await service.get(passportId, {
          query: { listingId }
        });
        setData(result);
      } catch (e) {
        console.error('Error loading analytics:', e);
        setError(e.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    if (passportId && listingId) {
      loadAnalytics();
    }
  }, [passportId, listingId, isAdmin]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-12">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
        <p className="text-center text-gray-500 mt-4">Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!data?.hasAccess) {
    return <UpgradePrompt reason={data?.reason} />;
  }

  const { metrics, computedAt, passport, listing } = data;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{passport?.title}</h3>
            <p className="text-sm text-gray-500">
              Analytics for <span className="font-medium">{listing?.name}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Last updated</p>
            <p className="text-sm text-gray-600">{formatDate(computedAt)}</p>
          </div>
        </div>

        {/* Passport summary stats */}
        <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{passport?.totalParticipants}</p>
            <p className="text-xs text-gray-500">Total Participants</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{passport?.totalListings}</p>
            <p className="text-xs text-gray-500">Listings in Passport</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {passport?.status === 'active' ? (
                <span className="text-green-600">Active</span>
              ) : passport?.status === 'ended' ? (
                <span className="text-yellow-600">Ended</span>
              ) : (
                <span className="text-gray-600">{passport?.status}</span>
              )}
            </p>
            <p className="text-xs text-gray-500">Status</p>
          </div>
        </div>
      </div>

      {/* Main metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <VisitorConversionCard metrics={metrics?.visitorConversion} />
        <BenchmarkingCard metrics={metrics?.benchmarking} />
      </div>

      {/* Timing and Conversions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TimingHeatmap metrics={metrics?.timing} />
        <ConversionMetricsCard metrics={metrics} />
      </div>

      {/* Behavioral insights */}
      <BehavioralCard metrics={metrics?.behavioral} />

      {/* Cross-visitation summary */}
      {metrics?.crossVisitation && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Cross-Visitation Insights</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-3xl font-bold text-gray-900">{metrics.crossVisitation.avgOtherStops}</p>
              <p className="text-sm text-gray-600">Avg. other stops your visitors made</p>
              <p className="text-xs text-gray-400 mt-1">
                Your visitors are exploring the passport
              </p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{metrics.crossVisitation.avgOverlapPct}%</p>
              <p className="text-sm text-gray-600">Journey coverage</p>
              <p className="text-xs text-gray-400 mt-1">
                % of passport stops your visitors complete on average
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
